import type { LegendListRef } from '@legendapp/list/react-native';
import { useRouter } from 'expo-router';
import { useRef, useState } from 'react';
import { Platform, type ScrollViewProps } from 'react-native';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import { useQuery, useSubscription } from 'urql';
import Paginator from '@/components/Custom/Paginator';
import Placeholder from '@/components/Custom/Placeholder';
import SearchBar from '@/components/Custom/SearchBar';
import PeopleListItem from '@/components/People/PeopleListItem';
import PeopleRowActions from '@/components/People/PeopleRowActions';
import PeopleToolbar from '@/components/People/PeopleToolbar';
import ActionButton from '@/components/UI/ActionButton';
import Badge from '@/components/UI/Badge';
import Checkbox from '@/components/UI/Checkbox';
import Section from '@/components/UI/Section';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import List from '@/components/Unistyles/List';
import { graphql, readFragment } from '@/gql/graphql';
import useDebounce from '@/hooks/useDebounce';
import usePaginatedList from '@/hooks/usePaginatedList';
import {
  useSelectionClear,
  useSelectionSelectedIds,
  useSelectionStore,
  useSelectionToggle,
} from '@/hooks/useSelection';
import useUserId from '@/hooks/useUserId';
import { getOrganizationMemberSettingsActions } from '@/screens/OrganizationMemberUpdate/OrganizationMemberUpdate.utils';
import { useMemberFilterValues } from '@/stores/useMemberFilterStore';
import useRootStore from '@/stores/useRootStore';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';
import {
  getOrganizationMemberAttributeColumnId,
  getOrganizationMembersWhere,
  type OrganizationMemberRow,
  toOrganizationMemberRow,
} from './OrganizationDetailMembers.utils';
import OrganizationMemberBadgeAssignmentMenu from './OrganizationMemberBadgeAssignmentMenu';
import OrganizationMemberFilterMenu from './OrganizationMemberFilterMenu';

const PeopleList = withUnistyles(List<OrganizationMemberRow>, (theme) => ({
  contentPadding: {
    paddingTop: theme.space('md'),
    paddingBottom: theme.space('xl'),
  },
}));

const MEMBER_PAGE_LIMIT = 25;
const MEMBER_PREVIEW_LIMIT = 5;
const NO_SELECTED_BADGE_ID = '00000000-0000-0000-0000-000000000000';
const scrollInsetProps: Pick<
  ScrollViewProps,
  'contentInsetAdjustmentBehavior'
> =
  Platform.OS === 'ios' ? { contentInsetAdjustmentBehavior: 'automatic' } : {};

const OrganizationMembersFragment = graphql(/* GraphQL */ `
  fragment OrganizationMembers on event {
    id
    owner_id
    permissions
    event_people(
      where: $memberWhere
      limit: $memberLimit
      offset: $memberOffset
      order_by: [{ person: { name: asc } }, { person_id: asc }]
    ) {
      event_id
      person_id
      role
      person {
        id
        name
        is_managed
        avatar_id
        color
        selected_badge_assignments: badge_assignments(
          where: {
            event_id: { _eq: $organizationId }
            badge_id: { _eq: $selectedBadgeId }
            badge: { is_stat: { _eq: false } }
          }
          order_by: { created_at: desc }
          limit: 1
        ) @include(if: $hasSelectedBadge) {
          id
          badge_assignee_attributes(
            order_by: { badge_attribute: { order: asc } }
          ) {
            id
            badge_attribute_id
            value
            formatted_value
          }
        }
        badge_assignments(
          where: {
            event_id: { _eq: $organizationId }
            badge: { is_stat: { _eq: false } }
          }
          order_by: { created_at: desc }
          limit: $memberPreviewLimit
        ) {
          id
          preview_value
          badge {
            id
            title
            color
          }
        }
      }
    }
    event_people_aggregate(where: $memberWhere) {
      aggregate {
        count
      }
    }
  }
`);

const GetOrganizationMembers = graphql(/* GraphQL */ `
  query GetOrganizationMembers(
    $organizationId: uuid!
    $memberWhere: event_person_bool_exp!
    $memberLimit: Int!
    $memberOffset: Int!
    $memberPreviewLimit: Int!
    $selectedBadgeId: uuid!
    $hasSelectedBadge: Boolean!
  ) {
    event_by_pk(id: $organizationId) {
      ...OrganizationMembers
    }
    selected_badge: badge_by_pk(
      id: $selectedBadgeId
    ) @include(if: $hasSelectedBadge) {
      id
      color
      badge_attributes(order_by: { order: asc }) {
        id
        title
      }
    }
  }
`, [OrganizationMembersFragment]);

const WatchOrganizationMembers = graphql(/* GraphQL */ `
  subscription WatchOrganizationMembers(
    $organizationId: uuid!
    $memberWhere: event_person_bool_exp!
    $memberLimit: Int!
    $memberOffset: Int!
    $memberPreviewLimit: Int!
    $selectedBadgeId: uuid!
    $hasSelectedBadge: Boolean!
  ) {
    event_by_pk(id: $organizationId) {
      ...OrganizationMembers
    }
  }
`, [OrganizationMembersFragment]);

function OrganizationAddPeopleButton({
  organizationId,
}: {
  organizationId: string;
}) {
  const { navigate } = useRouter();

  return (
    <ActionButton
      forceLabel
      icon="person-add-outline"
      label="Add People"
      testID="OrganizationDetailAddMembersButton"
      variant="success"
      onPress={() =>
        navigate({
          pathname: '/organization/[organizationId]/person/add',
          params: { organizationId },
        })
      }
    />
  );
}

export default function OrganizationDetailPeople() {
  const userId = useUserId();

  const { push } = useRouter();
  const organizationId = useRootStore((state) => state.organizationId);
  const filterKey = `organization-people:${organizationId ?? ''}`;
  const memberFilter = useMemberFilterValues(filterKey);
  const listRef = useRef<LegendListRef>(null);
  const { value, debouncedValue, onChangeText, reset } = useDebounce(
    DEFAULT_SEARCH_MINIMUM,
  );
  const paginated = usePaginatedList({
    limit: MEMBER_PAGE_LIMIT,
    listRef,
  });
  const memberWhere = getOrganizationMembersWhere({
    badgeIds: memberFilter.badgeIds,
    organizationId: organizationId || '',
    searchTerm: debouncedValue,
  });
  const hasSelectedBadge = memberFilter.badgeIds.length === 1;
  const selectedBadgeId = hasSelectedBadge
    ? (memberFilter.badgeIds[0] ?? NO_SELECTED_BADGE_ID)
    : NO_SELECTED_BADGE_ID;
  const variables = {
    organizationId: organizationId || '',
    memberWhere,
    memberLimit: paginated.limit,
    memberOffset: paginated.offset,
    memberPreviewLimit: MEMBER_PREVIEW_LIMIT,
    selectedBadgeId,
    hasSelectedBadge,
  };
  const [{ data, fetching }] = useQuery({
    query: GetOrganizationMembers,
    variables,
    requestPolicy: 'cache-and-network',
    pause: !organizationId,
  });
  useSubscription({
    query: WatchOrganizationMembers,
    variables,
    pause: !organizationId,
  });
  const organization = readFragment(
    OrganizationMembersFragment,
    data?.event_by_pk,
  );

  const selectedBadge =
    hasSelectedBadge && data?.selected_badge?.id === selectedBadgeId
      ? data.selected_badge
      : null;
  const rows = (organization?.event_people ?? []).map((member) =>
    toOrganizationMemberRow(member, organization?.owner_id),
  );
  const personSelectionStore = useSelectionStore<string>();
  const clearSelectedPeople = useSelectionClear(personSelectionStore);
  const selectedPersonIds = useSelectionSelectedIds(personSelectionStore);
  const toggleSelectedPerson = useSelectionToggle(personSelectionStore);
  const [personSelectionMode, setPersonSelectionMode] = useState(false);
  const isPersonSelectionMode =
    personSelectionMode || selectedPersonIds.length > 0;
  const peopleCount =
    organization?.event_people_aggregate.aggregate?.count ?? 0;
  const canAssign = Boolean(organization?.permissions?.assign_badge);
  const canInvite = Boolean(organization?.permissions?.create_invite);
  const emptyStateCopy =
    value.trim() || memberFilter.badgeIds.length
      ? 'No people match these filters'
      : 'No people to show';
  const selectedBadgeAttributes = selectedBadge?.badge_attributes ?? [];
  const endSelection = () => {
    clearSelectedPeople();
    setPersonSelectionMode(false);
  };
  const onPressRow = (row: OrganizationMemberRow) => {
    if (isPersonSelectionMode) {
      toggleSelectedPerson(
        row.personId,
        !selectedPersonIds.includes(row.personId),
      );
      return;
    }

    push({
      pathname: '/organization/member/[personId]',
      params: {
        organizationId: organizationId || '',
        personId: row.personId,
      },
    });
  };

  return (
    <Box flex>
      <Section>
        <PeopleToolbar
          actions={
            <>
              {canInvite && !isPersonSelectionMode ? (
                <OrganizationAddPeopleButton
                  organizationId={organizationId || ''}
                />
              ) : null}
              {canAssign && selectedPersonIds.length ? (
                <>
                  <OrganizationMemberBadgeAssignmentMenu
                    organizationId={organizationId || ''}
                    selectedPersonIds={selectedPersonIds}
                  />
                  <ActionButton
                    forceLabel
                    icon="close-outline"
                    label="Clear"
                    variant="secondary"
                    testID="OrganizationMembersClearSelectionButton"
                    onPress={endSelection}
                  />
                </>
              ) : null}
              {canAssign && !selectedPersonIds.length ? (
                <ActionButton
                  forceLabel
                  icon={
                    isPersonSelectionMode ? 'close-outline' : 'checkbox-outline'
                  }
                  label={isPersonSelectionMode ? 'Done' : 'Select'}
                  variant="secondary"
                  testID="OrganizationMembersSelectionButton"
                  onPress={() =>
                    isPersonSelectionMode
                      ? endSelection()
                      : setPersonSelectionMode(true)
                  }
                />
              ) : null}
              {!isPersonSelectionMode ? (
                <OrganizationMemberFilterMenu
                  activeFilterCount={memberFilter.badgeIds.length}
                  badgeIds={memberFilter.badgeIds}
                  organizationId={organizationId || ''}
                  targetKey={filterKey}
                  testIDPrefix="OrganizationMembers"
                  onOpenFilter={() => {
                    paginated.resetPage();
                    endSelection();
                  }}
                />
              ) : null}
            </>
          }
        />
        <Box direction="vertical" space="md" px="lg">
          <SearchBar
            value={value}
            onChangeText={(nextValue) => {
              paginated.resetPage();
              endSelection();
              onChangeText(nextValue);
            }}
            onPressCancel={() => {
              paginated.resetPage();
              endSelection();
              reset();
            }}
            placeholder="Search People..."
          />
        </Box>
      </Section>
      <Box flex testID="OrganizationMembersList">
        <PeopleList
          {...scrollInsetProps}
          ref={listRef}
          data={rows}
          fetching={fetching}
          extraData={[selectedPersonIds, isPersonSelectionMode, selectedBadge]}
          keyExtractor={(row) => row.personId}
          keyboardScroll={false}
          ListEmptyComponent={
            <Placeholder icon="people-outline" text={emptyStateCopy} />
          }
          ListFooterComponent={
            peopleCount > paginated.limit ? (
              <Paginator {...paginated} total={peopleCount} />
            ) : null
          }
          renderItem={({ item: row }) => {
            const actions = getOrganizationMemberSettingsActions({
              isManaged: row.isManaged,
              isOwner: row.personId === organization?.owner_id,
              isSelf: row.personId === userId,
              permissions: organization?.permissions,
            });
            const canEdit = Object.values(actions).some(Boolean);
            const selected = selectedPersonIds.includes(row.personId);
            return (
              <PeopleListItem
                avatarId={row.personAvatarId ?? ''}
                color={row.personColor ?? ''}
                name={row.personName}
                personTestID={`OrganizationMembersPerson-${row.personId}`}
                testID="OrganizationPersonListItem"
                selected={selected}
                onPress={() => onPressRow(row)}
                selection={
                  canAssign && isPersonSelectionMode ? (
                    <Checkbox
                      size="sm"
                      variant="info"
                      accessibilityLabel={`Select ${row.personName}`}
                      value={selected}
                      onValueChange={(checked) =>
                        toggleSelectedPerson(row.personId, checked)
                      }
                      style={styles.selectPerson}
                      testID={`OrganizationMembersSelectPerson-${row.personId}`}
                    />
                  ) : null
                }
                actions={
                  !isPersonSelectionMode && canEdit ? (
                    <Box alignSelf="center">
                      <PeopleRowActions
                        name={row.personName}
                        size="lg"
                        onEdit={() =>
                          push({
                            pathname:
                              '/organization/[organizationId]/member/[personId]/edit',
                            params: {
                              organizationId: organizationId || '',
                              personId: row.personId,
                            },
                          })
                        }
                      />
                    </Box>
                  ) : null
                }
                metadata={
                  row.roleLabel ? (
                    <Text
                      size="sm"
                      variant="typographySecondary"
                      testID={`OrganizationMembersRole-${row.personId}`}
                    >
                      {row.roleLabel.replaceAll(' · ', '\n')}
                    </Text>
                  ) : null
                }
                details={
                  selectedBadgeAttributes.length ? (
                    <Box style={styles.attributes}>
                      {selectedBadgeAttributes.map((attribute) => (
                        <Box key={attribute.id} style={styles.attribute}>
                          <Text size="xs" variant="typographySecondary">
                            {attribute.title ?? 'Attribute'}
                          </Text>
                          <Text
                            size="sm"
                            testID={`OrganizationMembersAttribute-${row.personId}-${attribute.id}`}
                          >
                            {row.attributeValues[
                              getOrganizationMemberAttributeColumnId(
                                attribute.id,
                              )
                            ]?.trim() || '—'}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  ) : row.badgePreviews.length ? (
                    <Box
                      direction="horizontal"
                      wrap
                      alignItems="flex-start"
                      space="sm"
                      mt="sm"
                      accessibilityLabel={row.badges}
                      testID={`OrganizationMembersBadges-${row.personId}`}
                    >
                      {row.badgePreviews.map((badge) => (
                        <Badge
                          key={badge.id}
                          color={badge.color}
                          maxWidth="100%"
                        >
                          <Badge.Text
                            numberOfLines={0}
                            style={styles.badgeText}
                          >
                            {badge.title ?? 'Badge'}
                          </Badge.Text>
                        </Badge>
                      ))}
                    </Box>
                  ) : null
                }
              />
            );
          }}
        />
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  selectPerson: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    minHeight: theme.size(11),
    width: theme.size(11),
  },
  attributes: {
    gap: theme.space('md'),
    marginTop: theme.space('sm'),
  },
  attribute: {
    minWidth: theme.size(24),
    maxWidth: '100%',
    flexShrink: 1,
  },
  badgeText: {
    flexShrink: 1,
  },
}));
